/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ProfileDetailsGate
 * ------------------
 * The web's first sign-in screen, like UpVibe's: when the signed-in athlete's
 * user_profile row lacks gender, height, weight or city (or does not exist yet), a
 * sheet asks for them — and for an optional mobile number — before the feed
 * can be used. It cannot be dismissed; it closes once the row is saved.
 *
 * Reads the same ['profile', 'row', userId] query the profile page uses, so
 * saving here refreshes that page too. A failed read shows nothing rather
 * than locking the athlete out.
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';
import { Icon } from '../../../components/feed/FeedParts';
import { fetchUserProfile, saveUserProfile, updateMobile } from './profileApi';
import {
  personalDetails, needsRequiredDetails, validateRequiredDetails, profileChanges, membershipMobile,
} from './profileModel';
import RequiredDetailsFields, { requiredDetailsForm } from './RequiredDetailsFields';
import '../../../styles/athleteProfile.css';

const api = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export default function ProfileDetailsGate({ userId }) {
  const rowQ = useQuery({
    queryKey: ['profile', 'row', userId],
    queryFn: ({ signal }) => fetchUserProfile(api, { userId, signal }),
    enabled: userId !== null,
    staleTime: 5 * 60 * 1000,
  });

  if (userId === null || !rowQ.isSuccess || !needsRequiredDetails(rowQ.data)) return null;
  // Keyed by the row, so a refetch that still lacks details starts from its values.
  return <DetailsSheet key={rowQ.data?.id || 'new'} userId={userId} row={rowQ.data} />;
}

function DetailsSheet({ userId, row }) {
  const queryClient = useQueryClient();
  const details = personalDetails(row);
  const [form, setForm] = useState(() => requiredDetailsForm(details));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateRequiredDetails(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    // profileChanges also reads age; leaving it empty sends none.
    const changes = profileChanges({ ...form, age: '' }, details);
    setSaving(true);
    setFormError('');
    try {
      await saveUserProfile(api, { userId, profileId: row?.id, changes });
      if ('mobile_number' in changes) {
        await updateMobile(api, membershipMobile(changes.mobile_number));
      }
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    } catch (err) {
      setFormError(err?.message || 'Could not save your details. Try again.');
      setSaving(false);
    }
  };

  return (
    <div className="ap-scrim ap-scrim-center">
      <form className="ap-sheet ap-gate" role="dialog" aria-modal="true"
        aria-labelledby="ap-gate-title" aria-describedby="ap-gate-sub" onSubmit={submit} noValidate>
        <div className="ap-sh-h">
          <div>
            <h2 id="ap-gate-title">Finish your profile</h2>
            <p id="ap-gate-sub" className="ap-gate-sub">
              We need your gender, height and weight to estimate your calories accurately,
              and your city to show you events nearby. It only takes a moment.
            </p>
          </div>
        </div>

        <div className="ap-sh-b">
          <div className="ap-fs">
            <RequiredDetailsFields form={form} set={set} errors={errors} idPrefix="ap-gate" />
          </div>
          {formError && <div className="ap-form-err" role="alert">{formError}</div>}
        </div>

        <div className="ap-sh-f">
          <button type="submit" className="fd-btn fd-btn-primary" disabled={saving}>
            <Icon name="check" />{saving ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
